// Libs
import classNames from 'classnames/bind';
import { Button, Grid2, Stack, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseSelect } from '~/components/common';
// Others
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_1000,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getListMasterData } from '~/thunks/masterData/masterDataThunk';
import { IQueryMasterData } from '~/utils/interface/masterData';
import { TypeMasterDataEnum, TypeTabCreateEstimateEnum } from '~/utils/enum/common';
import useDebounce from '~/utils/hooks/useDebounce';
import { IBaseOption } from '~/utils/interface/common';
import { IFormEstimate } from '~/utils/interface/estimate';
// Styles, images, icons
import styles from './CreateEstimateTagsTab.module.scss';

type Props = {
  onEdit: (data: IFormEstimate, type: string) => void;
  onCancelEdit: () => void;
};

const cx = classNames.bind(styles);

const CreateEstimateTagsTab = (props: Props) => {
  //#region Destructuring Props
  const { onEdit, onCancelEdit } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/estimate', 'common/base']);
  const dispatch = useAppDispatch();
  const { id: estimateId } = useParams();
  const {
    control,
    getValues,
    formState: { isDirty },
  } = useFormContext<IFormEstimate>();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [listTag, setListTag] = useState<IBaseOption[]>([]);
  const [tagSearchKey, setTagSearchKey] = useState<string>(EMPTY_STRING);
  const debounceTagSearchKey = useDebounce(tagSearchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  const handleGetListTag = useCallback(
    (params: IQueryMasterData) => {
      setIsLoading(true);

      dispatch(getListMasterData(params))
        .unwrap()
        .then((res) => {
          if (!res?.data) return;

          const { responses } = res?.data;

          if (responses) {
            const dataFormatted = responses?.map((item) => ({ label: item?.name, value: item?.id }));

            setListTag(dataFormatted);
          }
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch]
  );

  //#region Implement Hook
  useEffect(() => {
    const payload: IQueryMasterData = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
      type: TypeMasterDataEnum.TAG,
      ...(debounceTagSearchKey && { textSearch: debounceTagSearchKey }),
    };

    handleGetListTag(payload);
  }, [debounceTagSearchKey, handleGetListTag]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleTagSearchChange = (searchKey: string) => {
    setTagSearchKey(searchKey);
  };
  const handleSubmitFormJobTag = () => {
    const dataForm = getValues();
    const dataBody: IFormEstimate = {
      clientId: EMPTY_STRING,
      tagIds: dataForm?.tagIds || [],
    };

    onEdit && onEdit(dataBody, TypeTabCreateEstimateEnum.TAGS);
  };
  //#endregion Handle Function

  return (
    <Grid2 component='div' id='jobTagsComponent' className={cx('container')} container rowSpacing={'12px'}>
      <Grid2 size={'grow'} container>
        <Grid2 size={12}>
          <Typography color='primary' className={cx('title')}>
            {t('estimate_tags_title')}
          </Typography>
        </Grid2>
        <Grid2 size={12}>
          <Controller
            name='tagIds'
            control={control}
            render={({ field: { value, onChange } }) => (
              <BaseSelect
                value={value || EMPTY_STRING}
                onChange={onChange}
                options={listTag}
                isLoading={isLoading}
                multiple
                onSearchChange={handleTagSearchChange}
              />
            )}
          />
        </Grid2>
        {estimateId && (
          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'flex-end'}
            component={'div'}
            spacing={'12px'}
            className={cx('formFooter')}
          >
            <Button variant='outlined' onClick={onCancelEdit}>
              {t('common_action_cancel', { ns: 'common/base' })}
            </Button>

            <Button variant='contained' type='button' onClick={handleSubmitFormJobTag} disabled={!isDirty}>
              {t('common_action_save', { ns: 'common/base' })}
            </Button>
          </Stack>
        )}
      </Grid2>
    </Grid2>
  );
};

export default CreateEstimateTagsTab;
